package com.example.mystybag.common.ui.components.native

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import dev.icerock.moko.resources.ImageResource
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.common.extensions.ClickTracker
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.core.extensions.capitalizeFirst
import com.example.mystybag.common.ui.components.native.Texts.BodySmall
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p36
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p28
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.ui.theme.LocalTheme.p100
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.views.isDarkTheme

/**
 * Button types aligned with design system (Figma):
 * - [PRIMARY] Filled light green (Primary), dark green text (Primary Variant). Main CTA.
 * - [SECONDARY] Filled pale green (Primary Container), dark green text.
 * - [TERTIARY] Text only, dark green, underlined. No background.
 * - [SECONDARY_NEUTRAL] Filled light gray (Surface Container Lowest), dark green text.
 * - [DANGER] Filled light red (Error Container), dark red text. Destructive actions.
 *
 * Legacy aliases kept for backward compatibility: BLUE = PRIMARY, LIGHT_BLUE = Secondary style, SECONDARY_RED = DANGER.
 *
 * Figma Button (Primary, Large): 380×56, cornerRadius 100, padding 10, itemSpacing 10, label Title Medium 16 (24 line height).
 */
enum class DefaultButtonSize {
    /** Figma Large: height 56dp, cornerRadius 100, padding 10 */
    LARGE,
    MEDIUM,
    SMALL
}

enum class DefaultButtonType {
    /** Design: Primary – fill Primary (#AFF99F), text Primary Variant */
    PRIMARY,
    /** Design: Secondary – fill Primary Container (pale green), text Primary Variant */
    SECONDARY,
    /** Design: Tertiary – text only, underlined, Primary Variant */
    TERTIARY,
    /** Design: Secondary neutral – fill Surface Container Lowest (light gray), text Primary Variant */
    SECONDARY_NEUTRAL,
    /** Design: Danger – fill Error Container (light red), text dark red */
    DANGER,
    /** @deprecated Use PRIMARY */
    BLUE,
    /** @deprecated Use SECONDARY (pale green fill) */
    LIGHT_BLUE,
    /** Transparent background, primary-colored text; for low emphasis */
    OUTLINED,
    /** White/surface background with outline only (e.g. WelcomeView social buttons per Figma) */
    OUTLINED_SURFACE,
    /** @deprecated Use DANGER */
    SECONDARY_RED,
    WHITE,
    APPLE,
    FACEBOOK,
    EMAIL,
    GOOGLE
}

enum class CaptionCapitalization {
    None,
    All,
    Sentences
}

@Composable
fun DefaultButton(
    modifier        : Modifier              = Modifier,
    caption         : String,
    capitalization  : CaptionCapitalization = CaptionCapitalization.Sentences,
    type            : DefaultButtonType     = DefaultButtonType.PRIMARY,
    size            : DefaultButtonSize     = DefaultButtonSize.LARGE,
    enabled         : Boolean               = true,
    icon            : Painter?              = null,
    iconTint        : Color?                = null,
    textColor       : Color?                = null,
    backgroundColor : Color?                = null,
    identifier      : String?               = null,
    height          : Dp?                   = null,
    cornerRadius    : Dp?                   = null,
    /** When null, uses design-system padding for [size] (same value on all sides). */
    contentPadding  : PaddingValues?        = null,
    fillMaxWidth    : Boolean               = true,
    callback        : () -> Unit
) {
    @Composable
    fun getBorder(): BorderStroke? {
        return when (type) {
            DefaultButtonType.OUTLINED        -> BorderStroke(p1, AppTheme.current.colors.primary)
            DefaultButtonType.OUTLINED_SURFACE -> BorderStroke(p1, AppTheme.current.colors.outline)
            else                             -> null
        }
    }

    @Composable
    fun getBackground(): Color {
        return backgroundColor ?: when (type) {
            DefaultButtonType.PRIMARY,
            DefaultButtonType.BLUE       -> AppTheme.current.colors.primary
            DefaultButtonType.SECONDARY,
            DefaultButtonType.LIGHT_BLUE -> AppTheme.current.colors.primaryContainer
            DefaultButtonType.TERTIARY   -> Color.Transparent
            DefaultButtonType.SECONDARY_NEUTRAL -> AppTheme.current.colors.surfaceContainerLowest
            DefaultButtonType.DANGER,
            DefaultButtonType.SECONDARY_RED -> AppTheme.current.colors.errorContainer
            DefaultButtonType.WHITE         -> AppTheme.current.colors.background
            DefaultButtonType.OUTLINED      -> Color.Transparent
            DefaultButtonType.OUTLINED_SURFACE -> AppTheme.current.colors.surface
            DefaultButtonType.APPLE        -> AppTheme.current.colors.inverseSurface
            DefaultButtonType.FACEBOOK   -> AppTheme.current.colors.primary
            DefaultButtonType.EMAIL      -> AppTheme.current.colors.secondary
            DefaultButtonType.GOOGLE     -> AppTheme.current.colors.surface
            else                         -> AppTheme.current.colors.surfaceDim
        }
    }

    @Composable
    fun getTextColor(): Color {
        return textColor ?: when (type) {
            DefaultButtonType.PRIMARY,
            DefaultButtonType.BLUE       -> AppTheme.current.colors.onPrimary
            DefaultButtonType.SECONDARY   -> AppTheme.current.colors.primary
            DefaultButtonType.LIGHT_BLUE  -> AppTheme.current.colors.onPrimaryContainer
            DefaultButtonType.TERTIARY,
            DefaultButtonType.SECONDARY_NEUTRAL -> AppTheme.current.colors.onPrimaryContainer
            DefaultButtonType.DANGER,
            DefaultButtonType.SECONDARY_RED -> AppTheme.current.colors.onErrorContainer
            DefaultButtonType.WHITE          -> AppTheme.current.colors.onSurface
            DefaultButtonType.OUTLINED_SURFACE -> AppTheme.current.colors.onSurface
            DefaultButtonType.APPLE,
            DefaultButtonType.FACEBOOK     -> AppTheme.current.colors.onPrimary
            DefaultButtonType.EMAIL        -> AppTheme.current.colors.onSecondary
            DefaultButtonType.GOOGLE        -> AppTheme.current.colors.onSurface
            DefaultButtonType.OUTLINED     -> AppTheme.current.colors.primary
            else                          -> AppTheme.current.colors.onPrimaryContainer
        }
    }

    val bgColor = getBackground()
    val (sizeHeight, sizeCornerRadius, sizePadding) = when (size) {
        DefaultButtonSize.LARGE  -> Triple(p56, p100, p10)
        DefaultButtonSize.MEDIUM -> Triple(p48, p100, p10)
        DefaultButtonSize.SMALL  -> Triple(p40, p100, p10)
    }
    val buttonHeight = height ?: sizeHeight
    val buttonCornerRadius = cornerRadius ?: sizeCornerRadius
    val contentPaddingValue = sizePadding
    val resolvedContentPadding = contentPadding ?: PaddingValues(all = contentPaddingValue)
    val labelText = when (capitalization) {
        CaptionCapitalization.None          -> caption
        CaptionCapitalization.All           -> caption.uppercase()
        CaptionCapitalization.Sentences     -> caption.lowercase().capitalizeFirst()
    }
    val baseLabelColor = getTextColor()
    val resolvedTextColor = if (isDarkTheme.value && !enabled) AppTheme.current.colors.surfaceContainerHighest else baseLabelColor

    if (type == DefaultButtonType.TERTIARY) {
        Box(
            modifier = modifier
                .clickable(enabled = enabled) { ClickTracker.tryClick(identifier, callback) }
                .padding(horizontal = p16, vertical = p12)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(p10)
            ) {
                icon?.also {
                    Image(
                        painter     = it,
                        modifier    = Modifier.size(p20),
                        colorFilter = iconTint?.let { ColorFilter.tint(it, blendMode = BlendMode.SrcIn) }
                    )
                }
                Texts.TitleMedium(
                    text          = labelText,
                    color         = resolvedTextColor,
                    textDecoration = TextDecoration.Underline
                )
            }
        }
        return
    }

    Button(
        onClick         = { ClickTracker.tryClick(identifier, callback) },
        modifier        = modifier
            .then(if (fillMaxWidth) Modifier.fillMaxWidth() else Modifier)
            .height(buttonHeight)
            .clip(RoundedCornerShape(buttonCornerRadius))
            .background(
                color = if (enabled) bgColor else bgColor.copy(alpha = 0.4f),
                shape  = RoundedCornerShape(buttonCornerRadius)
            ),
        shape           = RoundedCornerShape(buttonCornerRadius),
        border          = getBorder(),
        colors          = ButtonDefaults.buttonColors(
            containerColor          = Color.Transparent,
            disabledContainerColor  = Color.Transparent,
            contentColor            = baseLabelColor,
            disabledContentColor    = if (isDarkTheme.value) {
                AppTheme.current.colors.surfaceContainerHighest
            } else {
                baseLabelColor.copy(alpha = 0.38f)
            }
        ),
        contentPadding  = resolvedContentPadding,
        enabled         = enabled
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(p10)
        ) {
            icon?.also {
                Image(
                    painter     = icon,
                    modifier    = Modifier.size(p20),
                    colorFilter = iconTint?.let { ColorFilter.tint(it, blendMode = BlendMode.SrcIn) }
                )
            }
            Texts.TitleMedium(text = labelText, color = resolvedTextColor)
        }
    }
}

@Composable
fun SmallButton(
    modifier            : Modifier      = Modifier,
    text                : String,
    height              : Dp            = p40,
    backgroundColor     : Color         = AppTheme.current.colors.surface,
    textColor           : Color         = AppTheme.current.colors.onSurfaceVariant,
    textStyle           : TextStyle     = AppTheme.current.typography.bodyLarge,
    textPadding         : Pair<Dp, Dp>  = Pair(p16, p8),
    enabled             : Boolean       = true,
    doOnClick           : (() -> Unit)? = {}
) {
    Box(
        modifier        = modifier
            .height(height)
            .clip(RoundedCornerShape(p40))
            .clickable(
                enabled = enabled
            ) {
                doOnClick?.invoke()
            }
            .background(color = backgroundColor)
            .padding(horizontal = textPadding.first, vertical = textPadding.second),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text    = text,
            color   = textColor,
            style   = textStyle
        )
    }
}

/** Circular minus/plus; default 56×56 dp. [Canvas] arms scale with [buttonSize] (same proportions as 56 dp). */
@Composable
fun CircularQuantityButton(
    symbol: String,
    backgroundColor: Color,
    contentColor: Color,
    enabled: Boolean,
    onClick: () -> Unit,
    buttonSize: Dp = p56
) {
    val density = LocalDensity.current
    val armDp = buttonSize * (12f / 56f)
    val armPx = with(density) { armDp.toPx() }
    val strokePx = with(density) { 2.dp.toPx() }
    val lineColor = if (enabled) contentColor else contentColor.copy(alpha = 0.38f)
    val rippleRound = buttonSize / 2
    Box(
        modifier = Modifier
            .size(buttonSize)
            .clip(CircleShape)
            .background(backgroundColor)
            // Default rippleClick uses padding(p12), shrinking the child and causing inconsistent first draw vs after recomposition.
            .then(
                if (enabled) {
                    Modifier.rippleClick(padding = p0, roundPadding = rippleRound, action = onClick)
                } else {
                    Modifier
                }
            ),
        contentAlignment = Alignment.Center
    ) {
        Canvas(modifier = Modifier.size(buttonSize)) {
            if (size.minDimension < 1f) return@Canvas
            val cx = size.width / 2f
            val cy = size.height / 2f
            when (symbol.trim()) {
                "+", "＋" -> {
                    drawLine(
                        color = lineColor,
                        start = Offset(cx - armPx, cy),
                        end = Offset(cx + armPx, cy),
                        strokeWidth = strokePx,
                        cap = StrokeCap.Round
                    )
                    drawLine(
                        color = lineColor,
                        start = Offset(cx, cy - armPx),
                        end = Offset(cx, cy + armPx),
                        strokeWidth = strokePx,
                        cap = StrokeCap.Round
                    )
                }
                else -> {
                    drawLine(
                        color = lineColor,
                        start = Offset(cx - armPx, cy),
                        end = Offset(cx + armPx, cy),
                        strokeWidth = strokePx,
                        cap = StrokeCap.Round
                    )
                }
            }
        }
    }
}

@Composable
fun SmallButtonWithImage(
    modifier                    : Modifier          = Modifier,
    imageModifier               : Modifier          = Modifier,
    text                        : String?           = null,
    leadingImage                : ImageResource?    = null,
    enabled                     : Boolean           = true,
    isSelected                  : Boolean           = false,
    height                      : Dp                = p36,
    doOnClick                   : (() -> Unit)?     = null,
    imageColor                  : Color?            = null,
    backgroundColor             : Color?            = null,
    useRawImage                 : Boolean           = false,
    scale                       : Float             = 0.45f
) {
    val contentColor  = imageColor      ?: if (isSelected) AppTheme.current.colors.onPrimary            else if (!enabled) AppTheme.current.colors.onPrimaryContainer.copy(alpha = .4f) else AppTheme.current.colors.onPrimaryContainer
    val backColor     = backgroundColor ?: if (isSelected) AppTheme.current.colors.onPrimaryContainer   else if (!enabled) AppTheme.current.colors.primaryContainer.copy(alpha = .4f)   else AppTheme.current.colors.primaryContainer
    Box(
        modifier            = modifier
            .height(height)
            .widthIn(min = height)
            .clip(shape = if (text != null) RoundedCornerShape(height / 2) else CircleShape)
            .clickable(enabled = enabled, onClick = { ClickTracker.tryClick(null, doOnClick) })
            .background(color = backColor)
            .padding(horizontal = if (text != null) p12 else p0),
        contentAlignment    = Alignment.Center
    ) {
        Row(
            verticalAlignment       = Alignment.CenterVertically,
            horizontalArrangement   = Arrangement.Center
        ) {
            leadingImage?.let { image ->
                Image(
                    modifier    = imageModifier.size(height * scale),
                    painter     = painterResource(image),
                    colorFilter = if (useRawImage) null else ColorFilter.tint(color = contentColor)
                )
            }

            text?.let { text ->
                BodySmall(
                    modifier = Modifier.padding(start = if (leadingImage != null) p6 else p0),
                    text     = text,
                    color    = contentColor
                )
            }
        }
    }
}